/**
 * Canonical profile photo field: professionalPhoto / ProfessionalPhoto.
 * Legacy business uploads used personalProfessionalPhoto — still read as fallback.
 */

export const PROFILE_PHOTO_FORM_KEY = "professionalPhoto";

/** FormData scalar keys to skip so URL strings are never posted as fake files. */
export const PROFILE_PHOTO_SKIP_KEYS = new Set([
  "professionalphoto",
  "personalprofessionalphoto",
  "iddocument",
  "businesslogo",
  "bannerimage",
]);

export function shouldSkipProfileFileKey(key: string): boolean {
  return PROFILE_PHOTO_SKIP_KEYS.has(key.trim().toLowerCase());
}

type PhotoSource = object | null | undefined;

function firstNonEmpty(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) return value.trim();
  }
  return "";
}

function asRecord(source: PhotoSource): Record<string, unknown> | null {
  if (!source || typeof source !== "object") return null;
  return source as Record<string, unknown>;
}

/**
 * Resolve a displayable profile photo URL from a user row, imageUrls bag,
 * GetUser `{ user, imageUrls }` response, or a merge of those.
 * Prefer canonical ProfessionalPhoto; fall back to legacy PersonalProfessionalPhoto.
 */
export function resolveProfessionalPhotoUrl(
  source: PhotoSource,
  fallback = "",
): string {
  const row = asRecord(source);
  if (!row) return fallback;

  // GetUser returns { user, imageUrls } — photo URLs are usually only on imageUrls.
  const nestedUser = asRecord(
    (row.user as object | undefined) || (row.User as object | undefined) || null,
  );
  const nested = asRecord(
    (row.imageUrls as object | undefined) ||
      (row.ImageUrls as object | undefined) ||
      (nestedUser?.imageUrls as object | undefined) ||
      (nestedUser?.ImageUrls as object | undefined) ||
      null,
  );

  return (
    firstNonEmpty(
      row.ProfessionalPhoto,
      row.professionalPhoto,
      nested?.ProfessionalPhoto,
      nested?.professionalPhoto,
      nestedUser?.ProfessionalPhoto,
      nestedUser?.professionalPhoto,
      row.PersonalProfessionalPhoto,
      row.personalProfessionalPhoto,
      nested?.PersonalProfessionalPhoto,
      nested?.personalProfessionalPhoto,
      nestedUser?.PersonalProfessionalPhoto,
      nestedUser?.personalProfessionalPhoto,
    ) || fallback
  );
}

/** True when the user has a real uploaded profile photo (not empty / default placeholder). */
export function hasProfessionalPhoto(source: PhotoSource): boolean {
  const url = resolveProfessionalPhotoUrl(source, "");
  if (!url) return false;
  const lower = url.toLowerCase();
  if (lower.endsWith("/user.png") || lower.endsWith("user.png")) return false;
  return true;
}
